import { useEffect, useRef } from "react";

/**
 * The Watzmann as a 3D dot sculpture. A heightfield is shaped from the
 * massif's real profile (seen from Berchtesgaden): the sharp Watzmannfrau
 * left, the deep notch with the jagged Watzmannkinder, then the great
 * pyramid of the main summit with its long south shoulder. The point
 * cloud sways slowly so the depth reads; the high summits catch a faint
 * pink alpenglow. No pointer interaction.
 */

// skyline traced column-by-column from the original Watzmann silhouette
// artwork (Watzmannfrau left, the Kinder in the saddle, the main summit
// pyramid right of center, the long shoulder falling away east)
// prettier-ignore
const HEIGHTS = [
  0.02, 0.02, 0.02, 0.015, 0.007, 0.042, 0.059, 0.07, 0.07, 0.075, 0.103,
  0.116, 0.132, 0.149, 0.164, 0.184, 0.202, 0.213, 0.213, 0.206, 0.193,
  0.217, 0.309, 0.357, 0.379, 0.406, 0.41, 0.401, 0.441, 0.48, 0.498,
  0.531, 0.529, 0.548, 0.542, 0.618, 0.629, 0.632, 0.627, 0.572, 0.452,
  0.423, 0.366, 0.377, 0.386, 0.401, 0.368, 0.355, 0.344, 0.338, 0.342,
  0.362, 0.421, 0.417, 0.353, 0.364, 0.375, 0.368, 0.327, 0.333, 0.34,
  0.318, 0.379, 0.388, 0.386, 0.325, 0.327, 0.331, 0.436, 0.504, 0.533,
  0.603, 0.664, 0.697, 0.781, 0.82, 0.82, 0.904, 0.978, 0.991, 0.982,
  0.963, 1, 0.998, 0.982, 0.961, 0.936, 0.908, 0.895, 0.86, 0.825, 0.785,
  0.776, 0.757, 0.717, 0.673, 0.636, 0.596, 0.599, 0.57, 0.524, 0.489,
  0.386, 0.357, 0.3, 0.41, 0.406, 0.388, 0.364, 0.342, 0.32, 0.298,
  0.279, 0.257, 0.226, 0.204, 0.173, 0.149, 0.116, 0.105,
];

const X_HALF = 4.2; // world half-length of the ridge
const Z_HALF = 1.7; // world half-depth of the massif
const PEAK_Y = 1.62; // world height of the highest summit
const X_STEPS = 200;
const Z_STEPS = 40;
const GLOW_Y = 0.98; // world height where alpenglow begins (both summits)
const CAMERA = 9;

function profileAt(u: number): number {
  const f = Math.max(0, Math.min(1, u)) * (HEIGHTS.length - 1);
  const i = Math.floor(f);
  const a = HEIGHTS[i];
  const b = HEIGHTS[Math.min(i + 1, HEIGHTS.length - 1)];
  return a + (b - a) * (f - i);
}

function hash2(ix: number, iz: number): number {
  let n = ix * 374761393 + iz * 668265263;
  n = (n ^ (n >> 13)) * 1274126177;
  return ((n ^ (n >> 16)) >>> 0) / 4294967295;
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

function noise2(x: number, z: number): number {
  const ix = Math.floor(x);
  const iz = Math.floor(z);
  const fx = smooth(x - ix);
  const fz = smooth(z - iz);
  const a = hash2(ix, iz);
  const b = hash2(ix + 1, iz);
  const c = hash2(ix, iz + 1);
  const d = hash2(ix + 1, iz + 1);
  return a + (b - a) * fx + (c - a) * fz + (a - b - c + d) * fx * fz;
}

/** terrain height at world (x, z) — ridge crest falling off into the flanks */
function heightAt(x: number, z: number): number {
  const u = (x + X_HALF) / (2 * X_HALF);
  const crest = profileAt(u) * PEAK_Y;
  // flank width grows with the crest so the big peak reads as a broad pyramid
  const width = 0.55 + 0.75 * (crest / PEAK_Y);
  const f = Math.abs(z) / width;
  const fall = Math.max(0, 1 - Math.pow(f, 1.7));
  let hgt = crest * fall;
  // rocky irregularity
  hgt *= 0.9 + 0.2 * noise2(x * 2.1, z * 2.3);
  return hgt;
}

interface IDot {
  x: number;
  y: number;
  z: number;
  glow: number; // 0 = stone, >0 = alpenglow strength
}

function buildMountain(): IDot[] {
  const dots: IDot[] = [];
  for (let zi = 0; zi <= Z_STEPS; zi++) {
    const z0 = (zi / Z_STEPS - 0.5) * 2 * Z_HALF;
    for (let xi = 0; xi <= X_STEPS; xi++) {
      const x0 = (xi / X_STEPS - 0.5) * 2 * X_HALF;
      // organic jitter so the grid never reads as a grid
      const x = x0 + (hash2(xi * 7, zi * 13) - 0.5) * 0.05;
      const z = z0 + (hash2(xi * 17, zi * 29) - 0.5) * 0.1;
      const y = heightAt(x, z);
      if (y < 0.06) continue; // keep only the mountain body
      const glow = y > GLOW_Y ? Math.min(1, (y - GLOW_Y) / (PEAK_Y - GLOW_Y)) : 0;
      dots.push({ x, y, z, glow });
    }
  }
  return dots;
}

function parseHex(v: string, fallback: [number, number, number]): [number, number, number] {
  const m = v.trim().match(/^#([0-9a-f]{6})$/i);
  if (!m) return fallback;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function parseTriplet(v: string, fallback: [number, number, number]): [number, number, number] {
  const parts = v.split(",").map((p) => Number(p.trim()));
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return fallback;
  return [parts[0], parts[1], parts[2]];
}

export function Backdrop() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduced =
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

    const dots = buildMountain();

    let raf = 0;
    let running = false;
    let w = 0;
    let h = 0;
    let dpr = 1;
    let t = 0;
    let last = 0;

    let base = { rgb: [23, 18, 16] as [number, number, number], a: 0.2 };
    let pink: [number, number, number] = [240, 40, 124];

    const readColors = () => {
      const cs = getComputedStyle(document.documentElement);
      base = {
        rgb: parseTriplet(cs.getPropertyValue("--bd-base-rgb"), base.rgb),
        a: Number(cs.getPropertyValue("--bd-base-a")) || base.a,
      };
      pink = parseHex(cs.getPropertyValue("--accent"), pink);
    };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);

      const baseRGB = `${base.rgb[0]},${base.rgb[1]},${base.rgb[2]}`;
      const pinkRGB = `${pink[0]},${pink[1]},${pink[2]}`;

      const cx = w / 2 - w * 0.02;
      const cy = h * 0.66; // vertical anchor of the massif's center of mass
      const scale = Math.min(w, h) * 0.17;
      const glowPulse = 0.65 + 0.35 * Math.sin(t * 0.35);

      // slow sway, never leaving the recognizable frontal view
      const yaw = Math.sin(t * 0.14) * 0.42;
      const pitch = 0.34 + Math.sin(t * 0.09) * 0.05; // looking gently down
      const cosY = Math.cos(yaw);
      const sinY = Math.sin(yaw);
      const cosX = Math.cos(pitch);
      const sinX = Math.sin(pitch);

      for (let i = 0; i < dots.length; i++) {
        const d = dots[i];
        const yc = d.y - 0.75; // recenter height range around the pivot

        const x1 = d.x * cosY + d.z * sinY;
        const z1 = -d.x * sinY + d.z * cosY;
        const y2 = yc * cosX - z1 * sinX;
        const z2 = yc * sinX + z1 * cosX;

        const persp = CAMERA / (CAMERA - z2);
        const sx = cx + x1 * scale * persp;
        const sy = cy - y2 * scale * persp;

        const depth = Math.max(0, Math.min(1, (z2 + 2.2) / 4.4));
        const size = 1.0 + depth * 1.1;
        const fade = 0.35 + depth * 0.65;

        if (d.glow > 0) {
          ctx.fillStyle = `rgba(${pinkRGB},${((0.3 + 0.45 * d.glow * glowPulse) * fade).toFixed(3)})`;
        } else {
          ctx.fillStyle = `rgba(${baseRGB},${(base.a * 1.5 * fade).toFixed(3)})`;
        }
        ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
      }
    };

    const loop = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      t += dt;
      draw();
      raf = requestAnimationFrame(loop);
    };
    const start = () => {
      if (running || reduced) return;
      running = true;
      last = performance.now();
      raf = requestAnimationFrame(loop);
    };
    const stop = () => {
      running = false;
      cancelAnimationFrame(raf);
    };

    const onVisibility = () => {
      if (document.hidden) stop();
      else start();
    };
    const onResize = () => {
      resize();
      if (reduced) draw();
    };

    readColors();
    resize();

    const mo = new MutationObserver(() => {
      readColors();
      if (reduced) draw();
    });
    mo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    window.addEventListener("resize", onResize);
    if (reduced) {
      draw(); // a single static frame
    } else {
      document.addEventListener("visibilitychange", onVisibility);
      if (!document.hidden) start();
    }

    return () => {
      stop();
      mo.disconnect();
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return <canvas ref={canvasRef} className="backdrop" aria-hidden="true" />;
}
